"use client";

import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useStorageQuery } from "../../hooks/storageQueryProvider";
import { Button } from "@/components/ui/button";
import { FolderCheck, FolderOpen, LoaderCircle, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { client } from "@/lib/hono";
import toast from "@/lib/toast";
import { useStorage } from "@/features/storage/useStorage";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { t } from "@/lib/localization";

export default function SelectedItemsMenu({
    containerId,
    adjustToSidebar,
}: {
    containerId?: string;
    adjustToSidebar?: boolean;
}) {
    const [mounted, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);
    if (!mounted) return null;

    const container = containerId
        ? document.getElementById(containerId)
        : document.body;
    if (!container) return null;

    return createPortal(
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 flex w-full justify-center items-center z-[50] pointer-events-none">
            <Content adjustToSidebar={adjustToSidebar} />
        </div>,
        container
    );
}

const Content = ({ adjustToSidebar }: { adjustToSidebar?: boolean }) => {
    const {
        selectedFiles,
        selectedFolders,
        setSelectedFiles,
        setSelectedFolders,
        parentFolder,
        settings,
        showSelectedItemsMenu,
    } = useStorageQuery();

    const [loadingMove, setLoadingMove] = useState(false);
    const openStorageExplorer = useStorage((state) => state.setExplorerOpen);
    const queryClient = useQueryClient();
    const mode = useMemo(
        () => settings.selectedItemsMenu.mode,
        [settings.selectedItemsMenu.mode]
    );
    const { user } = useAuth();

    const moveHandler = async () => {
        const currentParentFolderId = parentFolder?.id;

        // this is a guard for the /move endpoint - it will fail if there are nested selected folders
        for (const folder1 of selectedFolders) {
            for (const folder2 of selectedFolders) {
                if (
                    folder1.id !== folder2.id &&
                    folder1.parentId !== folder2.parentId
                ) {
                    toast.error(
                        t(
                            "storage.selected-items.error.move-different-locations"
                        ),
                        {
                            keyName:
                                "storage.selected-items.error.move-different-locations",
                        }
                    );
                    return;
                }
            }
        }

        openStorageExplorer(true, {
            description: t("storage.selected-items.move.description"),
            actionsButton: {
                createFolder: true,
            },
            folders: {
                onFolderClickActions: ["navigate-to-folder"],
            },
            selectedItemsMenu: {
                mode: "single-folder",
                buttons: {
                    custom: {
                        text: t("storage.selected-items.move.confirm"),
                        icon: FolderCheck,
                        onClick: async (selectedDestinationFolders) => {
                            if (selectedDestinationFolders.length) {
                                const destinationFolder =
                                    selectedDestinationFolders[0];
                                const libraryId = user?.libraryId;

                                if (!libraryId) {
                                    toast.error(t("toast.error.default"), {
                                        keyName: "toast.error.default",
                                    });
                                    return;
                                }

                                setLoadingMove(true);

                                const res =
                                    await client.api.storage.folder.move.$post({
                                        json: {
                                            currentParentFolderId,
                                            destinationParentFolderId:
                                                destinationFolder.id,
                                            foldersToMove: selectedFolders,
                                            folderFilesToMove:
                                                selectedFiles.map((file) => ({
                                                    contentId: file.id,
                                                    contentType: file.type,
                                                    libraryId,
                                                })),
                                        },
                                    });

                                const resJson = await res.json();
                                console.log({ resJson });

                                if (!res.ok) {
                                    toast.error(t("toast.error.default"), {
                                        keyName: "toast.error.default",
                                    });
                                    setLoadingMove(false);
                                    return;
                                }

                                toast.success(
                                    t("toast.success.changes-saved"),
                                    {
                                        keyName: "toast.success.changes-saved",
                                    }
                                );

                                queryClient.invalidateQueries({
                                    queryKey: ["get-storage-files"],
                                });
                                queryClient.invalidateQueries({
                                    queryKey: ["get-storage-folders"],
                                });

                                setLoadingMove(false);
                                setSelectedFiles([]);
                                setSelectedFolders([]);
                                openStorageExplorer(false);
                            }
                        },
                    },
                },
            },
        });
    };

    const cancelHandler = () => {
        setSelectedFiles([]);
        setSelectedFolders([]);
    };

    const customButtonHandler = () => {
        if (mode === "single-folder") {
            settings?.selectedItemsMenu.buttons?.custom?.onClick(
                parentFolder ? [parentFolder] : [],
                []
            );
        } else if (mode === "multiple-items" || mode === "single-file") {
            settings?.selectedItemsMenu.buttons?.custom?.onClick(
                selectedFolders,
                selectedFiles
            );
        }
    };

    let text = "";

    if (mode === "single-folder") {
        text = t("storage.selected-items.label.folder", undefined, {
            name: parentFolder?.name || "",
        });
    } else if (mode === "single-file") {
        if (selectedFiles.length > 0) {
            text = t("storage.selected-items.label.file", undefined, {
                name: selectedFiles[0].name,
            });
        }
    } else if (mode === "multiple-items") {
        if (selectedFiles.length > 0 && selectedFolders.length > 0) {
            text = t("storage.selected-items.label.mixed", undefined, {
                folders: selectedFolders.length,
                files: selectedFiles.length,
            });
        } else if (selectedFiles.length > 0) {
            text = t("storage.selected-items.label.files", undefined, {
                files: selectedFiles.length,
            });
        } else if (selectedFolders.length > 0) {
            text = t("storage.selected-items.label.folders", undefined, {
                folders: selectedFolders.length,
            });
        }
    }

    return (
        <AnimatePresence>
            {showSelectedItemsMenu && (
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 40 }}
                    transition={{ duration: 0.2 }}
                    className={cn(
                        "rounded-lg p-2 ps-4 bg-primary text-white text-xs sm:text-sm flex flex-row items-center gap-8 sm:gap-16 pointer-events-auto",
                        adjustToSidebar && "lg:ms-[240px]"
                    )}
                >
                    <p className="shrink-0 max-w-[250px] truncate">{text}</p>
                    <div className="flex flex-row gap-2">
                        {settings.selectedItemsMenu.buttons?.custom && (
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={customButtonHandler}
                                className="flex flex-row gap-1.5 text-xs sm:text-sm hover:bg-[#262e6a] hover:text-white"
                            >
                                <settings.selectedItemsMenu.buttons.custom.icon className="size-4 hidden sm:block" />
                                {settings.selectedItemsMenu.buttons.custom.text}
                            </Button>
                        )}

                        {settings?.selectedItemsMenu.buttons?.move && (
                            <Button
                                disabled={loadingMove}
                                size="sm"
                                variant="ghost"
                                onClick={moveHandler}
                                className="flex flex-row gap-1.5 text-xs sm:text-sm hover:bg-[#262e6a] hover:text-white"
                            >
                                {loadingMove ? (
                                    <LoaderCircle className="size-4 animate-spin" />
                                ) : (
                                    <>
                                        <FolderOpen className="size-4 hidden sm:block" />
                                        {t("storage.selected-items.move.action")}
                                    </>
                                )}
                            </Button>
                        )}
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={cancelHandler}
                            className="flex flex-row gap-1.5 text-xs sm:text-sm hover:bg-[#262e6a] hover:text-white"
                        >
                            <X className="size-4 hidden sm:block" />
                            {t("storage.selected-items.cancel")}
                        </Button>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
